import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import ERPDataGrid from "../ERPDataGrid";
import "../ERPTheme.css";
import {baseUrl, ipAddress, userName} from "../Univ";
function Farmland() {
    // ===========================================
    // MASTER DATA
    // ===========================================
    const [owners, setOwners] = useState([]);
    const [crops, setCrops] = useState([]);
    const [varieties, setVarieties] = useState([]);
    // ===========================================
    // GRID DATA
    // ===========================================
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    // ===========================================
    // EDIT MODE
    // ===========================================
    const [editId, setEditId] = useState(null);
    // ===========================================
    // GOOGLE MAP LINK
    // ===========================================
    const [mapUrl, setMapUrl] = useState("");
    // ===========================================
    // FORM DATA
    // ===========================================
    const initialState = {
        owner: "",
        land_name: "",
        survey_no: "",
        patta_no: "",
        extent: "",
        unit: "ACRE",
        address: "",
        village: "",
        taluk: "",
        district: "",
        state: "",
        pincode: "",
        latitude: "",
        longitude: "",
        soil_type: "",
        water_source: "",
        current_crop: "",
        current_variety: "",
        season: "",
        status: "1"
    };
    const [formData, setFormData] = useState(initialState);
    // ===========================================
    // PAGE LOAD
    // ===========================================
    useEffect(() => {
        loadPage();
    }, []);
    // ===========================================
    // LOAD PAGE
    // ===========================================
    const loadPage = async () => {
        await Promise.all([
            fetchOwners(),
            fetchCrops(),
            fetchVarieties(),
            fetchFarmlands()
        ]);
    };
    // ===========================================
    // FETCH OWNERS
    // ===========================================
    const fetchOwners = async () => {
        try {
            const res = await axios.get(baseUrl + "land-owner/create/");
            setOwners(res.data);
        } catch (error) {
            console.log(error);
        }
    };
    // ===========================================
    // FETCH CROPS
    // ===========================================
    const fetchCrops = async () => {
        try {
            const res = await axios.get(baseUrl + "crop/");
            setCrops(res.data);
        } catch (error) {
            console.log(error);
        }
    };
    // ===========================================
    // FETCH VARIETIES
    // ===========================================
    const fetchVarieties = async () => {
        try {
            const res = await axios.get(baseUrl + "crop-variety/");
            setVarieties(res.data);
        } catch (error) {
            console.log(error);
        }
    };
    // ===========================================
    // FETCH FARMLANDS
    // ===========================================
    const fetchFarmlands = async () => {
        try {
            setLoading(true);
            const res = await axios.get(baseUrl + "farmland/");
            setRows(res.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };
    // ===========================================
    // HANDLE CHANGE
    // ===========================================
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };
    // ===========================================
    // RESET FORM
    // ===========================================
    const resetForm = () => {
        setEditId(null);
        setMapUrl("");
        setFormData(initialState);
    };
    // ===========================================
    // VALIDATION
    // ===========================================
    const validateForm = () => {
        if (!formData.owner) {
            Swal.fire("Owner",
                "Please select Land Owner",
                "warning");
            return false;
        }
        if (!formData.land_name) {
            Swal.fire("Land Name",
                "Enter Land Name",
                "warning");
            return false;
        }
        if (!formData.survey_no) {
            Swal.fire("Survey Number",
                "Enter Survey Number",
                "warning");
            return false;
        }
        if (!formData.extent) {
            Swal.fire("Extent",
                "Enter Land Extent",
                "warning");
            return false;
        }
        return true;
    };
    // ===========================================
    // SAVE / UPDATE
    // ===========================================
    const saveFarmland = async () => {
        if (!validateForm())
            return;
        try {
            const fd = new FormData();
            Object.keys(formData).forEach(key => {
                fd.append(key, formData[key]);
            });
            fd.append("addedby", userName);
            fd.append("ip_add", ipAddress);
            if (editId === null) {
                await axios.post(baseUrl + "farmland/",
                    fd);
                Swal.fire({
                    icon: "success",
                    title: "Farmland Saved",
                    timer: 1500,
                    showConfirmButton: false
                });
            } else {
                await axios.put(baseUrl +
                    "update-farmland/" +
                    editId +
                    "/",
                    fd);
                Swal.fire({
                    icon: "success",
                    title: "Farmland Updated",
                    timer: 1500,
                    showConfirmButton: false
                });
            }
            resetForm();
            fetchFarmlands();
        } catch (error) {
            console.log(error);
            Swal.fire("Error",
                "Unable to Save",
                "error");
        }
    };
    // ===========================================
    // DELETE FARMLAND
    // ===========================================
    const deleteFarmland = async (id) => {
        const result = await Swal.fire({
            title: "Delete Farmland?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete"
        });
        if (!result.isConfirmed)
            return;
        try {
            await axios.delete(baseUrl +
                "delete-farmland/" +
                id +
                "/");
            Swal.fire({
                icon: "success",
                title: "Deleted Successfully",
                timer: 1500,
                showConfirmButton: false
            });
            fetchFarmlands();
        } catch (error) {
            console.log(error);
            Swal.fire("Error",
                "Unable to delete farmland.",
                "error");
        }
    };
    // ===========================================
    // FETCH CURRENT GPS LOCATION
    // ===========================================
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            Swal.fire("Error",
                "GPS is not supported in this browser.",
                "error");
            return;
        }
        navigator.geolocation.getCurrentPosition(async (position) => {
                const latitude = Number(position.coords.latitude.toFixed(10));
                const longitude = Number(position.coords.longitude.toFixed(10));
                await reverseGeocode(latitude,
                    longitude);
            },
            () => {
                Swal.fire("Error",
                    "Unable to fetch current location.",
                    "error");
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            });
    };
    // ===========================================
    // REVERSE GEOCODE
    // ===========================================
    const reverseGeocode = async (latitude, longitude) => {
        try {
            const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`);
            const addr = response.data.address || {};
            setMapUrl(`https://www.google.com/maps?q=${latitude},${longitude}`);
            setFormData(prev => ({
                ...prev,
                latitude: latitude,
                longitude: longitude,
                address:
                    response.data.display_name || "",
                village:
                    addr.village ||
                    addr.hamlet ||
                    addr.suburb ||
                    "",
                taluk:
                    addr.county ||
                    "",
                district:
                    addr.state_district ||
                    addr.county ||
                    "",
                state:
                    addr.state ||
                    "",
                pincode:
                    addr.postcode ||
                    ""
            }));
            Swal.fire({
                icon: "success",
                title: "Location Retrieved",
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.log(error);
            Swal.fire("Error",
                "Unable to fetch address.",
                "error");
        }
    };
    // ===========================================
    // OPEN GOOGLE MAP
    // ===========================================
    const openGoogleMap = () => {
        if (!mapUrl) {
            Swal.fire("Warning",
                "Location not available.",
                "warning");
            return;
        }
        window.open(mapUrl,
            "_blank");
    };
    // ===========================================
    // FILTER VARIETY
    // ===========================================
    const filteredVarieties =
        varieties.filter(item =>
            String(item.crop.id) ===
            String(formData.current_crop));
    // ===========================================
    // DATAGRID COLUMNS
    // ===========================================
    const columns = [
        {
            field: "id",
            headerName: "ID",
            width: 70
        },
        {
            field: "owner",
            headerName: "Owner",
            width: 180,
            renderCell: (params) =>
                params.row.owner?.name || "-"
        },
        {
            field: "land_name",
            headerName: "Land Name",
            width: 180
        },
        {
            field: "survey_no",
            headerName: "Survey No",
            width: 140
        },
        {
            field: "extent",
            headerName: "Extent",
            width: 120
        },
        {
            field: "unit",
            headerName: "Unit",
            width: 100
        },
        {
            field: "village",
            headerName: "Village",
            width: 150
        },
        {
            field: "district",
            headerName: "District",
            width: 150
        },
        {
            field: "action",
            headerName: "Action",
            width: 120,
            sortable: false
        }
    ];
    // ===========================================
    // UI
    // ===========================================
    return (
        <section className="container-fluid mt-4">

            <div className="card shadow">

                <div className="card-header bg-success text-white">

                    <div className="d-flex justify-content-between align-items-center">

                        <h4 className="mb-0">

                            🌾 Farmland Registration

                        </h4>

                        <button

                            className="btn btn-light"

                            type="button"

                            onClick={resetForm}

                        >

                            New Registration

                        </button>

                    </div>

                </div>

                <div className="card-body">

                    {/* =======================================
                        LAND INFORMATION
                    ======================================== */}

                    <div className="card mb-4">

                        <div className="card-header bg-primary text-white">

                            <strong>

                                Land Information

                            </strong>

                        </div>

                        <div className="card-body">

                            <div className="row">

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Land Owner

                                    </label>

                                    <select

                                        className="form-select"

                                        name="owner"

                                        value={formData.owner}

                                        onChange={handleChange}

                                    >

                                        <option value="">

                                            Select Owner

                                        </option>

                                        {
                                            owners.map(owner => (
                                                <option

                                                    key={owner.id}

                                                    value={owner.id}

                                                >

                                                    {owner.name}

                                                </option>
                                            ))
                                        }

                                    </select>

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Land Name

                                    </label>

                                    <input

                                        className="form-control"

                                        name="land_name"

                                        value={formData.land_name}

                                        onChange={handleChange}

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Survey Number

                                    </label>

                                    <input

                                        className="form-control"

                                        name="survey_no"

                                        value={formData.survey_no}

                                        onChange={handleChange}

                                    />

                                </div>

                            </div>

                            <div className="row">

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Patta Number

                                    </label>

                                    <input

                                        className="form-control"

                                        name="patta_no"

                                        value={formData.patta_no}

                                        onChange={handleChange}

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Extent

                                    </label>

                                    <input

                                        type="number"

                                        className="form-control"

                                        name="extent"

                                        value={formData.extent}

                                        onChange={handleChange}

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Unit

                                    </label>

                                    <select

                                        className="form-select"

                                        name="unit"

                                        value={formData.unit}

                                        onChange={handleChange}

                                    >

                                        <option value="ACRE">

                                            Acre

                                        </option>

                                        <option value="HECTARE">

                                            Hectare

                                        </option>

                                        <option value="CENT">

                                            Cent

                                        </option>

                                        <option value="SQFT">

                                            Square Feet

                                        </option>

                                    </select>

                                </div>

                            </div>

                        </div>

                    </div>
                    {/* =======================================
                        GPS LOCATION
                    ======================================== */}

                    <div className="card mb-4">

                        <div className="card-header bg-success text-white">

                            <strong>

                                GPS Location

                            </strong>

                        </div>

                        <div className="card-body">

                            <div className="row mb-3">

                                <div className="col-md-12">

                                    <button

                                        type="button"

                                        className="btn btn-success me-2"

                                        onClick={getCurrentLocation}

                                    >

                                        📍 Fetch Current Location

                                    </button>

                                    <button

                                        type="button"

                                        className="btn btn-primary"

                                        onClick={openGoogleMap}

                                    >

                                        🌍 View on Google Map

                                    </button>

                                </div>

                            </div>

                            <div className="row">

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Latitude

                                    </label>

                                    <input

                                        className="form-control"

                                        name="latitude"

                                        value={formData.latitude}

                                        onChange={handleChange}

                                        readOnly

                                    />

                                </div>

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Longitude

                                    </label>

                                    <input

                                        className="form-control"

                                        name="longitude"

                                        value={formData.longitude}

                                        onChange={handleChange}

                                        readOnly

                                    />

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* =======================================
                        ADDRESS DETAILS
                    ======================================== */}

                    <div className="card mb-4">

                        <div className="card-header bg-info text-white">

                            <strong>

                                Address Details

                            </strong>

                        </div>

                        <div className="card-body">

                            <div className="row">

                                <div className="col-md-12 mb-3">

                                    <label className="form-label">

                                        Address

                                    </label>

                                    <textarea

                                        rows="3"

                                        className="form-control"

                                        name="address"

                                        value={formData.address}

                                        onChange={handleChange}

                                    />

                                </div>

                            </div>

                            <div className="row">

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Village

                                    </label>

                                    <input

                                        className="form-control"

                                        name="village"

                                        value={formData.village}

                                        onChange={handleChange}

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Taluk

                                    </label>

                                    <input

                                        className="form-control"

                                        name="taluk"

                                        value={formData.taluk}

                                        onChange={handleChange}

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        District

                                    </label>

                                    <input

                                        className="form-control"

                                        name="district"

                                        value={formData.district}

                                        onChange={handleChange}

                                    />

                                </div>

                            </div>

                            <div className="row">

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        State

                                    </label>

                                    <input

                                        className="form-control"

                                        name="state"

                                        value={formData.state}

                                        onChange={handleChange}

                                    />

                                </div>

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Pincode

                                    </label>

                                    <input

                                        className="form-control"

                                        name="pincode"

                                        value={formData.pincode}

                                        onChange={handleChange}

                                    />

                                </div>

                            </div>

                        </div>

                    </div>
                    {/* =======================================
                        AGRICULTURE DETAILS
                    ======================================== */}

                    <div className="card mb-4">

                        <div className="card-header bg-warning">

                            <strong>

                                Agriculture Details

                            </strong>

                        </div>

                        <div className="card-body">

                            <div className="row">

                                {/* Soil Type */}

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Soil Type

                                    </label>

                                    <select

                                        className="form-select"

                                        name="soil_type"

                                        value={formData.soil_type}

                                        onChange={handleChange}

                                    >

                                        <option value="">Select</option>

                                        <option value="Black Soil">Black Soil</option>

                                        <option value="Red Soil">Red Soil</option>

                                        <option value="Clay Soil">Clay Soil</option>

                                        <option value="Loamy Soil">Loamy Soil</option>

                                        <option value="Sandy Soil">Sandy Soil</option>

                                        <option value="Alluvial Soil">Alluvial Soil</option>

                                        <option value="Laterite Soil">Laterite Soil</option>

                                        <option value="Saline Soil">Saline Soil</option>

                                    </select>

                                </div>

                                {/* Water Source */}

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Water Source

                                    </label>

                                    <select

                                        className="form-select"

                                        name="water_source"

                                        value={formData.water_source}

                                        onChange={handleChange}

                                    >

                                        <option value="">Select</option>

                                        <option value="Borewell">Borewell</option>

                                        <option value="Open Well">Open Well</option>

                                        <option value="Canal">Canal</option>

                                        <option value="River">River</option>

                                        <option value="Lake">Lake</option>

                                        <option value="Tank">Tank</option>

                                        <option value="Rainfed">Rainfed</option>

                                        <option value="Drip Irrigation">Drip Irrigation</option>

                                        <option value="Sprinkler">Sprinkler</option>

                                    </select>

                                </div>

                                {/* Season */}

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Season

                                    </label>

                                    <select

                                        className="form-select"

                                        name="season"

                                        value={formData.season}

                                        onChange={handleChange}

                                    >

                                        <option value="">Select</option>

                                        <option value="Kuruvai">Kuruvai</option>

                                        <option value="Samba">Samba</option>

                                        <option value="Navarai">Navarai</option>

                                        <option value="Kharif">Kharif</option>

                                        <option value="Rabi">Rabi</option>

                                        <option value="Summer">Summer</option>

                                        <option value="Whole Year">Whole Year</option>

                                    </select>

                                </div>

                            </div>

                            <div className="row">

                                {/* Current Crop */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Current Crop

                                    </label>

                                    <select

                                        className="form-select"

                                        name="current_crop"

                                        value={formData.current_crop}

                                        onChange={handleChange}

                                    >

                                        <option value="">

                                            Select Crop

                                        </option>

                                        {
                                            crops.map(crop => (
                                                <option

                                                    key={crop.id}

                                                    value={crop.id}

                                                >

                                                    {crop.name}

                                                </option>
                                            ))
                                        }

                                    </select>

                                </div>

                                {/* Current Variety */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Current Variety

                                    </label>

                                    <select

                                        className="form-select"

                                        name="current_variety"

                                        value={formData.current_variety}

                                        onChange={handleChange}

                                    >

                                        <option value="">

                                            Select Variety

                                        </option>

                                        {
                                            filteredVarieties.map(variety => (
                                                <option

                                                    key={variety.id}

                                                    value={variety.id}

                                                >

                                                    {variety.variety_name}

                                                </option>
                                            ))
                                        }

                                    </select>

                                </div>

                            </div>

                        </div>

                    </div>
                    {/* =======================================
                        LAND DOCUMENTS
                    ======================================== */}

                    <div className="card mb-4">

                        <div className="card-header bg-secondary text-white">

                            <strong>

                                Land Documents

                            </strong>

                        </div>

                        <div className="card-body">

                            <div className="row">

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Patta Document

                                    </label>

                                    <input

                                        type="file"

                                        className="form-control"

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Chitta Document

                                    </label>

                                    <input

                                        type="file"

                                        className="form-control"

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Adangal Document

                                    </label>

                                    <input

                                        type="file"

                                        className="form-control"

                                    />

                                </div>

                            </div>

                            <div className="row">

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Sale Deed

                                    </label>

                                    <input

                                        type="file"

                                        className="form-control"

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        FMB Sketch

                                    </label>

                                    <input

                                        type="file"

                                        className="form-control"

                                    />

                                </div>

                                <div className="col-md-4 mb-3">

                                    <label className="form-label">

                                        Land Photo

                                    </label>

                                    <input

                                        type="file"

                                        className="form-control"

                                    />

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* =======================================
                        ACTION BUTTONS
                    ======================================== */}

                    <div className="text-center mb-4">

                        <button

                            className="btn btn-success me-2"

                            type="button"

                            onClick={saveFarmland}

                        >

                            💾 Save Farmland

                        </button>

                        <button

                            className="btn btn-secondary"

                            type="button"

                            onClick={resetForm}

                        >

                            Reset

                        </button>

                    </div>

                </div>

            </div>


            {/* =======================================
                FARMLAND LIST
            ======================================== */}

            <div className="card shadow mt-4">

                <div className="card-header bg-dark text-white">

                    <strong>

                        Registered Farmlands

                    </strong>

                </div>

                <div className="card-body">

                    <ERPDataGrid

                        rows={rows}

                        columns={columns.map(col =>
                            col.field === "action"
                                ? {
                                    ...col,
                                    renderCell: (params) => (
                                        <button

                                            className="btn btn-danger btn-sm"

                                            onClick={() =>
                                                deleteFarmland(params.row.id)
                                            }

                                        >

                                            Delete

                                        </button>
                                    )
                                }
                                : col)}

                        loading={loading}

                        autoHeight

                        pageSizeOptions={[10, 25, 50, 100]}
                        
                        sx={{
                            boxShadow: 3,
                            border: 1,
                            borderColor: "#ddd"
                        }}

                    />

                </div>

            </div>

        </section>
    );
}
export default Farmland;